import { useState, useEffect } from 'react';
import * as Dialog from '@radix-ui/react-dialog';
import { X, Plus, Trash2 } from 'lucide-react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import api from '@/lib/axios';
import { formatCurrency } from '@/lib/utils';
import { getErrorMessage } from '@/lib/errors';

const ESTADOS = [
  { value: 'borrador', label: 'Borrador' },
  { value: 'enviado', label: 'Enviado' },
  { value: 'aprobado', label: 'Aprobado' },
  { value: 'en_proceso', label: 'En proceso' },
  { value: 'completado', label: 'Completado' },
  { value: 'cancelado', label: 'Cancelado' },
];

type Item = { descripcion: string; cantidad: string; precioUnitario: string };
const emptyItem = (): Item => ({ descripcion: '', cantidad: '1', precioUnitario: '' });

const emptyForm = {
  pacienteId: '', consultaId: '', folio: '', descripcion: '',
  estado: 'borrador', fechaEntrega: '',
};

type Props = {
  open: boolean;
  onOpenChange: (v: boolean) => void;
  proveedorId: string;
  presupuesto?: any;
};

export function PresupuestoFormDialog({ open, onOpenChange, proveedorId, presupuesto }: Props) {
  const qc = useQueryClient();
  const isEdit = !!presupuesto;
  const [form, setForm] = useState(emptyForm);
  const [items, setItems] = useState<Item[]>([emptyItem()]);

  const { data: pacientes = [] } = useQuery<any[]>({
    queryKey: ['pacientes-select'],
    queryFn: () => api.get('/api/pacientes').then(r => r.data),
  });

  useEffect(() => {
    if (presupuesto) {
      setForm({
        pacienteId: presupuesto.pacienteId ?? '',
        consultaId: presupuesto.consultaId ?? '',
        folio: presupuesto.folio ?? '',
        descripcion: presupuesto.descripcion ?? '',
        estado: presupuesto.estado ?? 'borrador',
        fechaEntrega: presupuesto.fechaEntrega ? presupuesto.fechaEntrega.slice(0, 10) : '',
      });
      setItems(
        presupuesto.items?.length
          ? presupuesto.items.map((i: any) => ({
              descripcion: i.descripcion,
              cantidad: String(i.cantidad),
              precioUnitario: String(i.precioUnitario),
            }))
          : [emptyItem()],
      );
    } else {
      setForm(emptyForm);
      setItems([emptyItem()]);
    }
  }, [presupuesto, open]);

  const f = (field: keyof typeof emptyForm) =>
    (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>) =>
      setForm(p => ({ ...p, [field]: e.target.value }));

  const updateItem = (idx: number, field: keyof Item, value: string) =>
    setItems(prev => prev.map((it, i) => i === idx ? { ...it, [field]: value } : it));

  const addItem = () => setItems(prev => [...prev, emptyItem()]);
  const removeItem = (idx: number) => setItems(prev => prev.filter((_, i) => i !== idx));

  const total = items.reduce((s, it) => {
    const sub = (parseFloat(it.cantidad) || 0) * (parseFloat(it.precioUnitario) || 0);
    return s + sub;
  }, 0);

  const mut = useMutation({
    mutationFn: (data: any) =>
      isEdit
        ? api.patch(`/api/presupuestos-proveedor/${presupuesto.id}`, data).then(r => r.data)
        : api.post(`/api/proveedores/${proveedorId}/presupuestos`, data).then(r => r.data),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['presupuestos-proveedor', proveedorId] });
      qc.invalidateQueries({ queryKey: ['presupuestos-proveedor-all'] });
      toast.success(isEdit ? 'Presupuesto actualizado' : 'Presupuesto creado');
      onOpenChange(false);
    },
    onError: (err: any) => toast.error(getErrorMessage(err, 'Error al guardar')),
  });

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    mut.mutate({
      ...form,
      consultaId: form.consultaId || null,
      fechaEntrega: form.fechaEntrega || null,
      items: items
        .filter(it => it.descripcion.trim())
        .map(it => ({
          descripcion: it.descripcion,
          cantidad: parseFloat(it.cantidad) || 1,
          precioUnitario: parseFloat(it.precioUnitario) || 0,
        })),
    });
  };

  return (
    <Dialog.Root open={open} onOpenChange={onOpenChange}>
      <Dialog.Portal>
        <Dialog.Overlay className="fixed inset-0 bg-black/40 backdrop-blur-sm z-40" />
        <Dialog.Content className="fixed left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 z-50 bg-white rounded-2xl shadow-xl w-full max-w-2xl max-h-[92vh] overflow-y-auto p-6 focus:outline-none">
          <div className="flex items-center justify-between mb-5">
            <Dialog.Title className="text-lg font-semibold text-text-primary">
              {isEdit ? 'Editar presupuesto' : 'Nuevo presupuesto'}
            </Dialog.Title>
            <Dialog.Close className="p-1.5 text-gray-400 hover:text-gray-600 rounded-lg hover:bg-gray-100 transition-colors">
              <X size={18} />
            </Dialog.Close>
          </div>

          <form onSubmit={handleSubmit} className="space-y-5">
            {/* Meta */}
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="block text-sm font-medium text-text-primary mb-1">Paciente <span className="text-red-500">*</span></label>
                <select required value={form.pacienteId} onChange={f('pacienteId')}
                  className="w-full px-3 py-2.5 rounded-lg border border-gray-200 text-sm focus:outline-none focus:ring-2 focus:ring-primary-300 bg-white">
                  <option value="">Seleccionar</option>
                  {(pacientes as any[]).map(p => (
                    <option key={p.id} value={p.id}>{p.nombre} {p.apellidoPaterno}</option>
                  ))}
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium text-text-primary mb-1">Estado</label>
                <select value={form.estado} onChange={f('estado')}
                  className="w-full px-3 py-2.5 rounded-lg border border-gray-200 text-sm focus:outline-none focus:ring-2 focus:ring-primary-300 bg-white">
                  {ESTADOS.map(e => <option key={e.value} value={e.value}>{e.label}</option>)}
                </select>
              </div>
            </div>

            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="block text-sm font-medium text-text-primary mb-1">Folio</label>
                <input value={form.folio} onChange={f('folio')} placeholder="Ej. LAB-001"
                  className="w-full px-3 py-2.5 rounded-lg border border-gray-200 text-sm focus:outline-none focus:ring-2 focus:ring-primary-300" />
              </div>
              <div>
                <label className="block text-sm font-medium text-text-primary mb-1">Fecha de entrega</label>
                <input type="date" value={form.fechaEntrega} onChange={f('fechaEntrega')}
                  className="w-full px-3 py-2.5 rounded-lg border border-gray-200 text-sm focus:outline-none focus:ring-2 focus:ring-primary-300" />
              </div>
            </div>

            <div>
              <label className="block text-sm font-medium text-text-primary mb-1">Descripción general</label>
              <textarea rows={2} value={form.descripcion} onChange={f('descripcion')} placeholder="Descripción del trabajo o pedido..."
                className="w-full px-3 py-2.5 rounded-lg border border-gray-200 text-sm focus:outline-none focus:ring-2 focus:ring-primary-300 resize-none" />
            </div>

            {/* Items */}
            <div>
              <div className="flex items-center justify-between mb-2">
                <label className="text-sm font-medium text-text-primary">Conceptos</label>
                <button type="button" onClick={addItem}
                  className="text-xs text-primary-600 hover:text-primary-700 flex items-center gap-1 font-medium">
                  <Plus size={13} /> Agregar línea
                </button>
              </div>

              <div className="space-y-2">
                <div className="grid grid-cols-12 gap-2 text-xs font-medium text-text-secondary px-1">
                  <span className="col-span-6">Descripción</span>
                  <span className="col-span-2 text-center">Cant.</span>
                  <span className="col-span-3 text-right">Precio unit.</span>
                  <span className="col-span-1" />
                </div>
                {items.map((it, idx) => (
                  <div key={idx} className="grid grid-cols-12 gap-2 items-center">
                    <input value={it.descripcion} onChange={e => updateItem(idx, 'descripcion', e.target.value)}
                      placeholder="Concepto..." required={idx === 0}
                      className="col-span-6 px-2.5 py-2 rounded-lg border border-gray-200 text-sm focus:outline-none focus:ring-2 focus:ring-primary-300" />
                    <input type="number" min="0.01" step="0.01" value={it.cantidad} onChange={e => updateItem(idx, 'cantidad', e.target.value)}
                      className="col-span-2 px-2.5 py-2 rounded-lg border border-gray-200 text-sm text-center focus:outline-none focus:ring-2 focus:ring-primary-300" />
                    <input type="number" min="0" step="0.01" value={it.precioUnitario} onChange={e => updateItem(idx, 'precioUnitario', e.target.value)}
                      placeholder="0.00"
                      className="col-span-3 px-2.5 py-2 rounded-lg border border-gray-200 text-sm text-right focus:outline-none focus:ring-2 focus:ring-primary-300" />
                    <button type="button" onClick={() => removeItem(idx)} disabled={items.length === 1}
                      className="col-span-1 flex items-center justify-center text-gray-300 hover:text-red-400 disabled:opacity-20">
                      <Trash2 size={14} />
                    </button>
                  </div>
                ))}
              </div>

              {total > 0 && (
                <div className="flex justify-end mt-3 pt-3 border-t border-gray-100">
                  <p className="text-sm font-bold text-text-primary">Total: {formatCurrency(total)}</p>
                </div>
              )}
            </div>

            <div className="flex gap-3 pt-2">
              <Dialog.Close className="flex-1 py-2.5 rounded-lg border border-gray-200 text-sm font-medium text-text-primary hover:bg-gray-50 transition-colors">
                Cancelar
              </Dialog.Close>
              <button type="submit" disabled={mut.isPending}
                className="flex-1 py-2.5 rounded-lg bg-primary-500 text-white text-sm font-medium hover:bg-primary-600 transition-colors disabled:opacity-60">
                {mut.isPending ? 'Guardando...' : isEdit ? 'Actualizar' : 'Crear presupuesto'}
              </button>
            </div>
          </form>
        </Dialog.Content>
      </Dialog.Portal>
    </Dialog.Root>
  );
}
